import {
  IDLE_MEDIA_SESSION,
  MEDIA_SESSION_ACTIONS,
  mediaSessionDiff,
  mediaSessionPublished,
  type MediaSessionActionDetail,
  type MediaSessionActionId,
  type MediaSessionSnapshot,
} from '@visious/core';
import { systemClock } from './clock';

/**
 * `navigator.mediaSession`, behind the one seam Android's `MediaSessionService` will implement —
 * §10 Phase 4.
 *
 * This is the *only* file in the app that touches the Media Session API, and the split is the same one
 * `player/port.ts` draws: core decides what the session should hold (`playback/mediaSession.ts`) and
 * this decides how to say it to one platform. §10's point about `MediaSessionService` is that the TV
 * needs the same shape, so a screen must never reach for `navigator.mediaSession` itself — there would
 * be nothing for Compose to substitute.
 *
 * Everything below the snapshot is defensive on purpose, because this API fails in a way that is
 * uniquely bad for a media app: it **throws** rather than declining. `setActionHandler` takes a WebIDL
 * enum, so an action a browser has not implemented is a `TypeError` and not a no-op; `setPositionState`
 * throws for a duration of `NaN`, for `position > duration` and for a `playbackRate` of zero — all
 * three of which a `<video>` produces in ordinary use, which is why core validates before a position
 * state exists at all. An unguarded call here would throw four times a second inside a `setInterval`
 * and take the transport bar's own sampling down with it.
 */

/** What an OS button asked for. The two nullable fields are the API's two optional ones. */
export type MediaSessionDispatch = (
  action: MediaSessionActionId,
  detail: MediaSessionActionDetail,
) => void;

/**
 * The seam. Deliberately narrow — publish an idempotent snapshot, or give the session back — because
 * that is the whole contract a `MediaSessionService` can also honour.
 */
export interface MediaSessionService {
  /** False where the platform has no media session at all. Publishing is then a no-op, not an error. */
  readonly available: boolean;
  /**
   * Hand over everything the session should hold. Cheap to call on every render: the diff decides what
   * is actually written, and an unchanged sample writes nothing.
   */
  publish(snapshot: MediaSessionSnapshot): void;
  /**
   * Give the session back: no metadata, nothing playing, no handlers.
   *
   * Not optional politeness. A page that navigates away without doing this leaves the OS showing a
   * video that is no longer on screen, and leaves a hardware play key wired to a handler whose player
   * has been destroyed.
   */
  release(): void;
}

const UNAVAILABLE: MediaSessionService = {
  available: false,
  publish: () => undefined,
  release: () => undefined,
};

function mediaSessionOf(): MediaSession | null {
  // `Navigator.mediaSession` is typed as always present, so the guard has to be a runtime `in` check
  // rather than a `?.` the compiler would call unnecessary. Safari had it behind a flag for years and
  // a WebView can be missing it entirely.
  if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return null;
  const session = navigator.mediaSession;
  return typeof session === 'object' && session !== null ? session : null;
}

/** `sizes` wants `"320x180"`, which is why core hands over numbers and this formats them (R7). */
function sizesOf(widthPx: number | null, heightPx: number | null): string | undefined {
  if (widthPx === null || heightPx === null) return undefined;
  if (widthPx <= 0 || heightPx <= 0) return undefined;
  return `${widthPx}x${heightPx}`;
}

export function createMediaSessionService(dispatch: MediaSessionDispatch): MediaSessionService {
  const found = mediaSessionOf();
  if (found === null) return UNAVAILABLE;
  // Re-bound rather than used through the narrowing: the three writers below are hoisted function
  // declarations, so the compiler cannot know they run after the guard and re-widens `found` to `| null`.
  const session: MediaSession = found;

  /** The shadow of what the OS holds. Folded field by field — see `mediaSessionPublished`. */
  let published: MediaSessionSnapshot | null = null;
  /** When the *position* was last handed over, so the drift check knows how far it has extrapolated. */
  let positionAtMs = systemClock.nowEpochMs();
  /**
   * Actions this browser refused, and the position setter if it refused too.
   *
   * Recorded rather than retried. Both refusals are permanent facts about the engine, and the caller
   * publishes at the transport's sampling rate — so retrying would mean catching the same `TypeError`
   * four times a second for as long as the video plays.
   */
  const refusedActions = new Set<MediaSessionActionId>();
  let positionRefused = false;
  /** The handlers currently registered, so a set that shrank actually clears the ones it dropped. */
  const registered = new Set<MediaSessionActionId>();

  function writeMetadata(snapshot: MediaSessionSnapshot): void {
    const metadata = snapshot.metadata;
    if (metadata === null) {
      session.metadata = null;
      return;
    }
    // A fresh `MediaMetadata` rather than mutating the existing one. Mutation happens to work in
    // Chrome, and the assignment is what the spec defines as the update — which is also why the diff
    // matters: a new object re-runs artwork selection and can re-fetch up to 266 KB.
    session.metadata = new MediaMetadata({
      title: metadata.title,
      artist: metadata.artist,
      ...(metadata.album === null ? {} : { album: metadata.album }),
      artwork: metadata.artwork.map((image) => {
        const sizes = sizesOf(image.widthPx, image.heightPx);
        // No `type`. See `mediaSessionArtwork`: a declared MIME type the browser disagrees with makes
        // it skip the image, and this API already declares dimensions Invidious does not always serve.
        return sizes === undefined ? { src: image.url } : { src: image.url, sizes };
      }),
    });
  }

  function writeActions(snapshot: MediaSessionSnapshot): void {
    for (const action of MEDIA_SESSION_ACTIONS) {
      const wanted = snapshot.actions.includes(action);
      if (wanted === registered.has(action)) continue;
      if (wanted && refusedActions.has(action)) continue;

      try {
        session.setActionHandler(action, wanted ? (details) => dispatch(action, detailOf(details)) : null);
        if (wanted) registered.add(action);
        else registered.delete(action);
      } catch {
        // A `TypeError` here is "this browser does not know that action", which is information rather
        // than a failure: the others still register, and the OS simply shows fewer buttons.
        refusedActions.add(action);
        registered.delete(action);
      }
    }
  }

  function writePosition(snapshot: MediaSessionSnapshot): boolean {
    if (positionRefused) return false;
    if (typeof session.setPositionState !== 'function') {
      positionRefused = true;
      return false;
    }

    try {
      if (snapshot.position === null) {
        // No argument is the API's own "forget the position", not `{}` and not zeroes.
        session.setPositionState();
      } else {
        session.setPositionState({
          duration: snapshot.position.durationSec,
          position: snapshot.position.positionSec,
          playbackRate: snapshot.position.playbackRate,
        });
      }
      positionAtMs = systemClock.nowEpochMs();
      return true;
    } catch {
      positionRefused = true;
      return false;
    }
  }

  return {
    available: true,

    publish(snapshot: MediaSessionSnapshot): void {
      const update = mediaSessionDiff({
        published,
        next: snapshot,
        sincePositionSec: Math.max(0, (systemClock.nowEpochMs() - positionAtMs) / 1000),
      });
      if (!update.any) return;

      if (update.metadata) writeMetadata(snapshot);
      // `status: null` never reaches here — `mediaSessionDiff` refuses it, because the no-MSE arm's
      // browser is the one reading the element and overwriting its accounting would be a lie.
      if (update.status && snapshot.status !== null) session.playbackState = snapshot.status;
      if (update.actions) writeActions(snapshot);
      const wrotePosition = update.position ? writePosition(snapshot) : false;

      // Only the writes that landed. A refused position must stay out of the shadow, or the next diff
      // measures drift against a value the OS was never given and concludes there is nothing to do.
      published = mediaSessionPublished(published, snapshot, { ...update, position: wrotePosition });
    },

    release(): void {
      // Through the same two writers, so the clear cannot drift from the publish — and unconditionally
      // rather than through the diff, because "what the OS holds" is exactly what we are abandoning.
      writeMetadata(IDLE_MEDIA_SESSION);
      session.playbackState = 'none';
      writeActions(IDLE_MEDIA_SESSION);
      writePosition(IDLE_MEDIA_SESSION);
      published = null;
    },
  };
}

/** The API's optional fields, narrowed to the two core reads. `undefined` and `null` both mean absent. */
function detailOf(details: MediaSessionActionDetails): MediaSessionActionDetail {
  return {
    seekOffsetSec: details.seekOffset ?? null,
    seekTimeSec: details.seekTime ?? null,
  };
}
